import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import HeaderBoardDropdown from './HeaderBoardDropdown';
import EditColumnsModel from './EditColumnsModel';
import { Dialog } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import {
  ArrowDown,
  ArrowUp,
  BarChart3,
  DollarSign,
  PencilLine,
  SlidersHorizontal,
  Target,
  Trash2,
} from 'lucide-react';
import { useCRM } from '../../../hooks/crm';

const ACTION_BUTTON_CLASS = cn(
  'rounded-full border-stone-200 bg-white px-3.5 text-xs font-medium text-stone-700 shadow-sm transition-colors',
  'hover:bg-stone-50 hover:text-stone-900'
);

const METRIC_CHIP_CLASS = cn(
  'inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 py-1.5',
  'text-xs font-medium text-stone-600 shadow-sm'
);

const CRMHeader = () => {
  const { t } = useTranslation();
  const {
    selectedBoard,
    selectedBoardId,
    selectedBoardTotalInvestorValues,
    setTargetAmount,
    clearTargetAmount,
  } = useCRM();

  const [isEditColumnsOpen, setIsEditColumnsOpen] = useState(false);
  const [isTargetAmountOpen, setIsTargetAmountOpen] = useState(false);
  const [targetAmount, setTargetAmountValue] = useState('');

  const parsedTargetAmount = Number(targetAmount);
  const canSaveTargetAmount = Boolean(
    selectedBoardId
    && targetAmount.trim() !== ''
    && !Number.isNaN(parsedTargetAmount)
  );

  const handleSaveTargetAmount = async () => {
    if (!canSaveTargetAmount) {
      return;
    }

    await setTargetAmount(selectedBoardId, parsedTargetAmount.toFixed(2));
    setIsTargetAmountOpen(false);
  };

  const progressPercentage = selectedBoard?.target_amount
    ? Math.min((selectedBoardTotalInvestorValues / selectedBoard.target_amount) * 100, 100)
    : 0;

  const visualProgressPercentage = progressPercentage > 0 && progressPercentage < 20
    ? 20
    : progressPercentage;

  const handleOpenTargetAmountDialog = () => {
    setTargetAmountValue(String(selectedBoard?.target_amount || ''));
    setIsTargetAmountOpen(true);
  };

  const handleAdjustTargetAmount = (delta) => {
    const baseValue = Number.isFinite(parsedTargetAmount) ? parsedTargetAmount : 0;
    const nextValue = Math.max(baseValue + delta, 0);
    setTargetAmountValue(nextValue.toFixed(2));
  };

  return (
    <div className="flex w-full flex-wrap items-center gap-3">
      <HeaderBoardDropdown />

      {selectedBoardId && selectedBoard && (
        <div className="flex flex-wrap items-center gap-2 lg:ml-6">
          {selectedBoard.target_amount ? (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-stone-200 bg-white px-3 py-2 shadow-sm">
              <span className={METRIC_CHIP_CLASS}>
                <BarChart3 className="h-3.5 w-3.5 text-stone-400" />
                <span>{selectedBoardTotalInvestorValues}</span>
              </span>

              <div className="flex min-w-[150px] items-center gap-2 px-1">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-stone-100">
                  <div
                    className="h-full rounded-full bg-stone-500 transition-[width]"
                    style={{ width: `${visualProgressPercentage}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-stone-500">
                  {`${progressPercentage.toFixed(2)}%`}
                </span>
              </div>

              <span className={METRIC_CHIP_CLASS}>
                <Target className="h-3.5 w-3.5 text-stone-400" />
                <span>{selectedBoard.target_amount}</span>
              </span>
            </div>
          ) : null}

          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenTargetAmountDialog}
            iconLeft={<DollarSign strokeWidth={1.7} className="h-3.5 w-3.5" />}
            className={ACTION_BUTTON_CLASS}
          >
            {selectedBoard.target_amount ? t('app:crm.t30') : t('app:crm.t32')}
          </Button>

          {selectedBoard.target_amount ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => clearTargetAmount(selectedBoardId)}
              iconLeft={<Trash2 className="h-3.5 w-3.5" />}
              className="rounded-full px-3 text-xs text-stone-500 hover:bg-stone-100 hover:text-stone-900"
            >
              {t('app:crm.t31')}
            </Button>
          ) : null}

          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditColumnsOpen(true)}
            iconLeft={<SlidersHorizontal className="h-3.5 w-3.5" />}
            className={ACTION_BUTTON_CLASS}
          >
            {t('app:crm.t33')}
          </Button>
        </div>
      )}

      <Dialog
        isOpen={isTargetAmountOpen}
        onClose={() => setIsTargetAmountOpen(false)}
        title={t('app:crm.t29')}
        subtitle={selectedBoard?.board_name}
        size="lg"
        stickyFooter
        closeOnOverlayClick
        overlayClassName="bg-white/10 backdrop-blur-[2px]"
        contentClassName="rounded-2xl border border-stone-200 shadow-2xl"
        bodyClassName="space-y-5"
        footerClassName="bg-white/90 backdrop-blur-[6px]"
        footerActions={(
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsTargetAmountOpen(false)}
              className="rounded-full border-stone-200 bg-white px-4 text-xs text-stone-700 hover:bg-stone-50"
            >
              {t('app:crm.t5')}
            </Button>
            <Button
              size="sm"
              onClick={handleSaveTargetAmount}
              disabled={!canSaveTargetAmount}
              iconLeft={<PencilLine className="h-3.5 w-3.5" />}
              className="rounded-full px-4 text-xs"
            >
              {t('app:crm.t4')}
            </Button>
          </div>
        )}
      >
        {selectedBoard?.target_amount ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className={METRIC_CHIP_CLASS}>
              <BarChart3 className="h-3.5 w-3.5 text-stone-400" />
              <span>{selectedBoardTotalInvestorValues}</span>
            </span>
            <span className={METRIC_CHIP_CLASS}>
              <Target className="h-3.5 w-3.5 text-stone-400" />
              <span>{selectedBoard.target_amount}</span>
            </span>
            <span className={METRIC_CHIP_CLASS}>
              <span>{`${progressPercentage.toFixed(2)}%`}</span>
            </span>
          </div>
        ) : null}

        <div className="rounded-2xl border border-stone-200 bg-stone-50/70 p-4">
          <div className="relative">
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">
              <DollarSign className="h-4 w-4" />
            </div>

            <Input
              multi={false}
              type="number"
              step="0.01"
              value={targetAmount}
              onChange={(event) => setTargetAmountValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSaveTargetAmount();
                }
              }}
              placeholder={t('app:crm.t29')}
              className="w-full border-stone-200 bg-white pl-9 pr-10 shadow-none"
            />

            <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 flex-col gap-1">
              <button
                type="button"
                onClick={() => handleAdjustTargetAmount(1)}
                className="flex h-4 w-6 items-center justify-center rounded-md bg-stone-200 text-xs text-stone-700 transition-colors hover:bg-stone-300"
              >
                <ArrowUp size={11} />
              </button>
              <button
                type="button"
                onClick={() => handleAdjustTargetAmount(-1)}
                className="flex h-4 w-6 items-center justify-center rounded-md bg-stone-200 text-xs text-stone-700 transition-colors hover:bg-stone-300"
              >
                <ArrowDown size={11} />
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      {selectedBoard && (
        <EditColumnsModel
          isOpen={isEditColumnsOpen}
          onClose={() => setIsEditColumnsOpen(false)}
          board={selectedBoard}
        />
      )}
    </div>
  );
};

export default CRMHeader;
